import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import {
  Upload,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Info,
  FileText,
  Loader2,
  Calendar as CalendarIcon,
  Database,
  Link2,
} from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';
import { format } from 'date-fns';

// ─── Types ──────────────────────────────────────────────────────────────────

interface ServiceRow {
  name: string;
  slug?: string;
  url?: string;
  description?: string;
  lang?: string;
  duration_minutes?: number;
  group_size_min?: number;
  group_size_max?: number;
  languages?: string[];
  inclusions?: string[];
  exclusions?: string[];
  price_model?: string;
  price_from_cents?: number;
  currency?: string;
}

interface ImportHistoryEntry {
  id: string;
  source: 'json' | 'csv';
  status: 'completed' | 'failed' | 'partial';
  total: number;
  succeeded: number;
  failed: number;
  date: string;
  errors?: string[];
}

interface BookingIntegration {
  id: string;
  name: string;
  icon: string;
  description: string;
  available: boolean;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const BOOKING_INTEGRATIONS: BookingIntegration[] = [
  {
    id: 'calendly',
    name: 'Calendly',
    icon: '📅',
    description: 'Import services and availability from Calendly event types',
    available: false,
  },
  {
    id: 'tripadvisor',
    name: 'TripAdvisor',
    icon: '🦉',
    description: 'Sync experiences and tours from your TripAdvisor listing',
    available: false,
  },
  {
    id: 'guesty',
    name: 'Guesty',
    icon: '🏠',
    description: 'Import property and experience listings from Guesty',
    available: false,
  },
  {
    id: 'getyourguide',
    name: 'GetYourGuide',
    icon: '🎫',
    description: 'Sync tours and activities from your GetYourGuide supplier account',
    available: false,
  },
];

const CSV_TEMPLATE_HEADER =
  'name,slug,url,description,lang,duration_minutes,group_size_min,group_size_max,languages,inclusions,exclusions,price_model,price_from_cents,currency';

// ─── Component ──────────────────────────────────────────────────────────────

export function ServiceInventoryImport() {
  const { selectedSite } = useSite();
  const [activeTab, setActiveTab] = useState('upload');

  // Upload state
  const [file, setFile] = useState<File | null>(null);
  const [parsedRows, setParsedRows] = useState<ServiceRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    succeeded: number;
    failed: number;
    errors: string[];
  } | null>(null);

  // History state
  const [history, setHistory] = useState<ImportHistoryEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const siteId = selectedSite?.id;

  // ── Load import history ───────────────────────────────────────────────────

  const loadHistory = useCallback(async () => {
    if (!siteId) return;
    setLoadingHistory(true);
    try {
      // History is stored locally since there's no dedicated backend endpoint yet
      const stored = localStorage.getItem(`service-import-history-${siteId}`);
      if (stored) {
        setHistory(JSON.parse(stored));
      }
    } finally {
      setLoadingHistory(false);
    }
  }, [siteId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const persistHistory = (entry: ImportHistoryEntry) => {
    const updated = [entry, ...history].slice(0, 50); // keep last 50
    setHistory(updated);
    if (siteId) {
      localStorage.setItem(`service-import-history-${siteId}`, JSON.stringify(updated));
    }
  };

  // ── File parsing ──────────────────────────────────────────────────────────

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    setFile(selected);
    setParseError(null);
    setParsedRows([]);
    setImportResult(null);

    try {
      const text = await selected.text();
      const ext = selected.name.split('.').pop()?.toLowerCase();

      if (ext === 'json') {
        const data = JSON.parse(text);
        const rows: ServiceRow[] = Array.isArray(data) ? data : [data];
        validateRows(rows);
        setParsedRows(rows);
      } else if (ext === 'csv') {
        const rows = parseCsv(text);
        validateRows(rows);
        setParsedRows(rows);
      } else {
        setParseError('Unsupported file type. Please upload a .json or .csv file.');
      }
    } catch (err: any) {
      setParseError(err.message || 'Failed to parse file');
    }
  };

  const parseCsv = (text: string): ServiceRow[] => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) throw new Error('CSV must have a header row and at least one data row');

    const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
    const nameIdx = headers.indexOf('name');
    if (nameIdx === -1) throw new Error('CSV must have a "name" column');

    return lines.slice(1).map((line, i) => {
      const values = line.split(',').map((v) => v.trim());
      const row: Record<string, any> = {};
      headers.forEach((h, idx) => {
        const val = values[idx];
        if (!val || val === '') return;
        // Parse array fields
        if (['languages', 'inclusions', 'exclusions'].includes(h)) {
          row[h] = val.split('|').map((v) => v.trim());
        } else if (['duration_minutes', 'group_size_min', 'group_size_max', 'price_from_cents'].includes(h)) {
          row[h] = parseInt(val, 10);
        } else {
          row[h] = val;
        }
      });
      if (!row.name) throw new Error(`Row ${i + 2}: name is required`);
      return row as ServiceRow;
    });
  };

  const validateRows = (rows: ServiceRow[]) => {
    if (rows.length === 0) throw new Error('No service rows found in file');
    const missing = rows.filter((r) => !r.name);
    if (missing.length > 0) throw new Error(`${missing.length} row(s) missing required "name" field`);
  };

  // ── Import execution ──────────────────────────────────────────────────────

  const handleImport = async () => {
    if (!siteId || parsedRows.length === 0) return;
    setImporting(true);
    setImportResult(null);

    let succeeded = 0;
    let failed = 0;
    const errors: string[] = [];

    for (const row of parsedRows) {
      try {
        const res = await authFetch(`/api/services/${siteId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(row),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({ error: res.statusText }));
          throw new Error(data.error || `HTTP ${res.status}`);
        }
        succeeded++;
      } catch (err: any) {
        failed++;
        errors.push(`"${row.name}": ${err.message}`);
      }
    }

    const result = { succeeded, failed, errors };
    setImportResult(result);
    setImporting(false);

    // Persist to history
    persistHistory({
      id: crypto.randomUUID(),
      source: file?.name.endsWith('.csv') ? 'csv' : 'json',
      status: failed === 0 ? 'completed' : succeeded === 0 ? 'failed' : 'partial',
      total: parsedRows.length,
      succeeded,
      failed,
      date: new Date().toISOString(),
      errors: errors.length > 0 ? errors : undefined,
    });
  };

  const handleReset = () => {
    setFile(null);
    setParsedRows([]);
    setParseError(null);
    setImportResult(null);
  };

  // ── No site selected ─────────────────────────────────────────────────────

  if (!selectedSite) {
    return (
      <div className="flex items-center justify-center h-64">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>Select a site to manage service imports.</AlertDescription>
        </Alert>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Service Inventory Import</h1>
        <p className="text-muted-foreground mt-1">
          Import services for {selectedSite.domain || selectedSite.name || 'this site'}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="upload">
            <Upload className="h-4 w-4 mr-2" />
            Manual Upload
          </TabsTrigger>
          <TabsTrigger value="integrations">
            <Link2 className="h-4 w-4 mr-2" />
            Booking Systems
          </TabsTrigger>
          <TabsTrigger value="history">
            <Clock className="h-4 w-4 mr-2" />
            Import History
          </TabsTrigger>
        </TabsList>

        {/* ── Manual Upload Tab ────────────────────────────────────────────── */}
        <TabsContent value="upload" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Upload Service Data
              </CardTitle>
              <CardDescription>
                Upload a JSON array or CSV file matching the services table schema.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* File input */}
              <div className="space-y-2">
                <Label htmlFor="service-file">Select file (.json or .csv)</Label>
                <Input
                  id="service-file"
                  type="file"
                  accept=".json,.csv"
                  onChange={handleFileSelect}
                  disabled={importing}
                />
              </div>

              {/* CSV template hint */}
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <strong>CSV format:</strong> Use pipe (|) to separate array values in languages, inclusions, and exclusions columns.
                  <br />
                  <code className="text-xs mt-1 block bg-muted p-2 rounded">
                    {CSV_TEMPLATE_HEADER}
                  </code>
                </AlertDescription>
              </Alert>

              {/* Parse error */}
              {parseError && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{parseError}</AlertDescription>
                </Alert>
              )}

              {/* Preview */}
              {parsedRows.length > 0 && !importResult && (
                <div className="space-y-3">
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Database className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {parsedRows.length} service{parsedRows.length !== 1 ? 's' : ''} ready to import
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={handleReset} disabled={importing}>
                        Cancel
                      </Button>
                      <Button size="sm" onClick={handleImport} disabled={importing}>
                        {importing ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Importing...
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-2" />
                            Import {parsedRows.length} Services
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Preview table */}
                  <div className="border rounded-lg overflow-auto max-h-64">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          <th className="text-left p-2">Name</th>
                          <th className="text-left p-2">Duration</th>
                          <th className="text-left p-2">Price</th>
                          <th className="text-left p-2">Language</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedRows.slice(0, 20).map((row, i) => (
                          <tr key={i} className="border-t">
                            <td className="p-2 font-medium">{row.name}</td>
                            <td className="p-2 text-muted-foreground">
                              {row.duration_minutes ? `${row.duration_minutes} min` : '-'}
                            </td>
                            <td className="p-2 text-muted-foreground">
                              {row.price_from_cents != null
                                ? `${(row.price_from_cents / 100).toFixed(2)} ${row.currency || 'EUR'}`
                                : '-'}
                            </td>
                            <td className="p-2 text-muted-foreground">{row.lang || '-'}</td>
                          </tr>
                        ))}
                        {parsedRows.length > 20 && (
                          <tr className="border-t">
                            <td colSpan={4} className="p-2 text-center text-muted-foreground">
                              ... and {parsedRows.length - 20} more
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Import result */}
              {importResult && (
                <div className="space-y-3">
                  <Separator />
                  <Alert variant={importResult.failed === 0 ? 'default' : 'destructive'}>
                    {importResult.failed === 0 ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <AlertTriangle className="h-4 w-4" />
                    )}
                    <AlertDescription>
                      <strong>
                        {importResult.succeeded} imported, {importResult.failed} failed
                      </strong>
                      {importResult.errors.length > 0 && (
                        <ul className="mt-2 space-y-1 text-xs">
                          {importResult.errors.slice(0, 10).map((err, i) => (
                            <li key={i} className="flex items-start gap-1">
                              <XCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                              {err}
                            </li>
                          ))}
                          {importResult.errors.length > 10 && (
                            <li className="text-muted-foreground">
                              ... and {importResult.errors.length - 10} more errors
                            </li>
                          )}
                        </ul>
                      )}
                    </AlertDescription>
                  </Alert>
                  <Button variant="outline" size="sm" onClick={handleReset}>
                    Import Another File
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Booking Systems Tab ──────────────────────────────────────────── */}
        <TabsContent value="integrations" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {BOOKING_INTEGRATIONS.map((integration) => (
              <Card key={integration.id} className="relative">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{integration.icon}</span>
                    <div>
                      <CardTitle className="text-base">{integration.name}</CardTitle>
                      <CardDescription className="text-sm">
                        {integration.description}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Badge variant="secondary">Coming Soon</Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* ── Import History Tab ───────────────────────────────────────────── */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5" />
                Import History
              </CardTitle>
              <CardDescription>
                Past service imports for this site
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : history.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No imports yet. Upload a JSON or CSV file to get started.
                </div>
              ) : (
                <div className="space-y-3">
                  {history.map((entry) => (
                    <div
                      key={entry.id}
                      className="flex items-center justify-between border rounded-lg p-3"
                    >
                      <div className="flex items-center gap-3">
                        <StatusIcon status={entry.status} />
                        <div>
                          <div className="font-medium text-sm">
                            {entry.source.toUpperCase()} import — {entry.total} service{entry.total !== 1 ? 's' : ''}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {format(new Date(entry.date), 'PPp')}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={entry.status === 'completed' ? 'default' : entry.status === 'failed' ? 'destructive' : 'secondary'}>
                          {entry.succeeded}/{entry.total} OK
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function StatusIcon({ status }: { status: ImportHistoryEntry['status'] }) {
  switch (status) {
    case 'completed':
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    case 'failed':
      return <XCircle className="h-5 w-5 text-red-500" />;
    case 'partial':
      return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
  }
}
